import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { DPSLayout } from '../../components/dps/layout/DPSLayout';
import { ScoreCircle } from '../../components/dps/overview/ScoreCircle';
import { CategoryCard } from '../../components/dps/overview/CategoryCard';
import { DashboardStats } from '../../components/dps/overview/DashboardStats';
import { PriorityActions } from '../../components/dps/overview/PriorityActions';
import { ComparisonCard } from '../../components/results/ComparisonCard';
import { dpsTheme } from '../../styles/dpsTheme';
import PDFDocument from '../../components/results/PDFDocument';
import { pdf } from '@react-pdf/renderer';
import toast from 'react-hot-toast';

interface DiagnosticData {
  id: string;
  user_id: string;
  diagnostic_data: any;
  security_score: number;
  category_scores: {
    physicalProtection: number;
    surveillance: number;
    deterrence: number;
  };
  recommendations: any[];
  created_at: string;
}

export const DPSOverview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [diagnostic, setDiagnostic] = useState<DiagnosticData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState<any[]>([]);
  const [initialScore, setInitialScore] = useState<number>(0);
  const [actionStats, setActionStats] = useState({
    completed: 0,
    inProgress: 0,
    planned: 0,
    total: 0,
  });

  useEffect(() => {
    const loadDiagnostic = async () => {
      if (!id) return;

      try {
        const { data, error } = await supabase
          .from('diagnostics')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;

        if (data) {
          setDiagnostic(data);

          // Récupérer le score initial depuis le premier snapshot
          const { data: snapshotsData } = await supabase
            .from('score_snapshots')
            .select('score')
            .eq('diagnostic_id', id)
            .order('created_at', { ascending: true })
            .limit(1);

          if (snapshotsData && snapshotsData.length > 0) {
            setInitialScore(snapshotsData[0].score);
          } else {
            // Fallback si pas de snapshot : utiliser le score actuel
            setInitialScore(data.security_score || 0);
          }

          const { data: actionsData } = await supabase
            .from('improvement_actions_history')
            .select('*')
            .eq('diagnostic_id', id)
            .order('priority', { ascending: true });

          if (actionsData) {
            setActions(actionsData);

            const completed = actionsData.filter(a => a.status === 'completed').length;
            const inProgress = actionsData.filter(a => a.status === 'in_progress').length;
            const planned = actionsData.filter(a => a.status === 'planned').length;

            setActionStats({
              completed,
              inProgress,
              planned,
              total: actionsData.length,
            });
          }
        }
      } catch (error) {
        console.error('Error loading diagnostic:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDiagnostic();
  }, [id]);

  const handleDownloadPDF = async () => {
    if (!diagnostic) return;

    try {
      const diagnosticData = diagnostic.diagnostic_data;
      if (!diagnosticData) {
        console.error('Diagnostic data is missing');
        return;
      }

      const blob = await pdf(
        <PDFDocument
          formData={diagnosticData}
          improvementActions={actions}
          companyName={userProfile?.company_name}
          companyLogoUrl={userProfile?.company_logo_url ?? undefined}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DPS-${diagnosticData.city || 'rapport'}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error, JSON.stringify(error));
      toast.error('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  if (loading) {
    return (
      <DPSLayout reportId={id || ''}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ color: dpsTheme.colors.text.muted }}>Chargement...</div>
        </div>
      </DPSLayout>
    );
  }

  if (!diagnostic) {
    return (
      <DPSLayout reportId={id || ''}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ color: dpsTheme.colors.text.muted }}>Diagnostic non trouvé</div>
        </div>
      </DPSLayout>
    );
  }

  const diagnosticData = diagnostic.diagnostic_data || {};
  const categoryScores = diagnostic.category_scores || {
    physicalProtection: 0,
    surveillance: 0,
    deterrence: 0,
  };
  const currentScore = diagnostic.security_score || 0;
  const progression = currentScore - initialScore;

  const completedActions = actions.filter(a => a.status === 'completed');
  const budgetSpent = completedActions.reduce((sum, a) => sum + (Number(a.actual_cost) || Number(a.estimated_cost) || 0), 0);
  const remainingActions = actions.filter(a => a.status !== 'completed');
  const budgetRemaining = remainingActions.reduce((sum, a) => sum + (Number(a.estimated_cost) || 0), 0);

  const priorityActions = actions
    .filter(a => a.status !== 'completed')
    .map(a => ({
      id: a.id,
      title: a.action_title,
      description: a.description || '',
      priority: a.priority as 'high' | 'medium' | 'low',
      cost: Number(a.estimated_cost) || 0,
      points: Number(a.score_impact) || 0,
    }));

  const housingTypeLabels: Record<string, string> = {
    house: 'Maison',
    apartment: 'Appartement',
  };

  return (
    <DPSLayout reportId={id || ''} onDownloadPDF={handleDownloadPDF}>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <section className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 backdrop-blur-xl border border-blue-500/20 rounded-2xl p-6 sm:p-8 lg:p-10 mb-6 lg:mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
            Diagnostic de Performance Sécuritaire
          </h1>
          <p className="text-base sm:text-lg text-gray-400 mb-4 sm:mb-6">
            Rapport complet
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-sm sm:text-base text-gray-500">
            <div className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>{diagnosticData.city || 'Ville non spécifiée'}, {diagnosticData.postalCode || 'CP'}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              <span>{housingTypeLabels[diagnosticData.housingType] || diagnosticData.housingType || 'Non spécifié'}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>Créé le {new Date(diagnostic.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
          <div className="lg:col-span-1">
            <ScoreCircle score={currentScore} />
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
            <CategoryCard name="Protection physique" score={categoryScores.physicalProtection} categoryType="protection" />
            <CategoryCard name="Surveillance" score={categoryScores.surveillance} categoryType="surveillance" />
            <CategoryCard name="Dissuasion" score={categoryScores.deterrence} categoryType="dissuasion" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6 mb-6 lg:mb-8">
          <div className="lg:col-span-3">
            <DashboardStats
              initialScore={initialScore}
              currentScore={currentScore}
              progression={progression}
              actionStats={actionStats}
              budgetSpent={budgetSpent}
              budgetRemaining={budgetRemaining}
            />
          </div>
          <div className="lg:col-span-2">
            <PriorityActions actions={priorityActions} reportId={id || ''} />
          </div>
        </div>

        <div className="mb-6 lg:mb-8">
          <ComparisonCard
            userScore={currentScore}
            housingType={diagnosticData.housingType || 'apartment'}
            showDetailedMessage={false}
          />
        </div>
      </main>
    </DPSLayout>
  );
};